import React from "react";
import { MessageSquare, X, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating chat-bubble that embeds the live HelloSir.ai agent on our own
 * landing page. End-users on hellosir.ai can chat (text + voice) with our
 * production agent — proving the product live.
 *
 * The agent ID is resolved server-side via `/api/public/landing-agent`
 * which a super admin configures from the admin panel.
 */
export default function LandingChatBubble() {
  const [open, setOpen] = React.useState(false);
  const [agentId, setAgentId] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    fetch(`${API}/api/public/landing-agent`)
      .then((r) => r.json())
      .then((d) => { if (alive) { setAgentId(d.agent_id || null); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  // Listen for messages from the iframe — auto-collapse on idle timeout.
  const iframeRef = React.useRef(null);
  React.useEffect(() => {
    const onMsg = (e) => {
      if (!e?.data) return;
      const type = typeof e.data === "string" ? e.data : e.data.type;
      if (type === "hellosir:idle-timeout" || type === "hellosir:close") setOpen(false);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // When the user collapses the floating bubble, tell the iframe to terminate
  // voice immediately so it doesn't keep listening / talking in the background.
  const handleToggle = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      if (!next) {
        try { iframeRef.current?.contentWindow?.postMessage("hellosir:host-close", "*"); } catch { /* noop */ }
      }
      return next;
    });
  };

  // Until an agent is configured we hide the bubble entirely to avoid a broken UX.
  if (!loaded || !agentId) return null;

  const widgetUrl = `${API.replace(/\/$/, "")}/widget/${agentId}?embed=1`;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleToggle}
        aria-label={open ? "Close chat" : "Chat with HelloSir.ai"}
        data-testid="landing-chat-bubble-toggle"
        className="fixed z-[60] bottom-6 right-6 w-16 h-16 rounded-full shadow-[0_18px_40px_-12px_rgba(0,68,255,0.55)] flex items-center justify-center text-white transition-all duration-300 hover:scale-110 active:scale-95"
        style={{
          background: open
            ? "linear-gradient(135deg,#0F1115 0%,#1E293B 100%)"
            : "linear-gradient(135deg,#0044FF 0%,#7C3AED 100%)",
        }}
      >
        {open ? <X className="w-7 h-7" /> : (
          <span className="relative">
            <MessageSquare className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5">
              <span className="absolute inset-0 rounded-full bg-[#10B981] animate-ping opacity-75" />
              <span className="relative inline-flex w-3.5 h-3.5 rounded-full bg-[#10B981] ring-2 ring-white" />
            </span>
          </span>
        )}
      </button>

      {/* Teaser callout while closed */}
      {!open && (
        <div
          className="fixed z-[59] bottom-7 right-24 max-w-[260px] px-4 py-2.5 rounded-[8px] bg-white shadow-lg border border-[#E2E8F0] hidden md:flex items-center gap-2.5 animate-hs-float"
          aria-hidden
        >
          <Sparkles className="w-4 h-4 text-[#0044FF] shrink-0" />
          <div className="text-xs text-[#0F1115] leading-snug">
            <span className="font-bold">Try me live</span> — ask anything about HelloSir.ai.
          </div>
        </div>
      )}

      {/* Chat panel */}
      <div
        className={`fixed z-[60] bottom-24 right-6 transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
        data-testid="landing-chat-bubble-panel"
      >
        <div className="w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-9rem)] rounded-[12px] overflow-hidden bg-white shadow-2xl border border-[#E2E8F0]">
          <iframe
            ref={iframeRef}
            src={widgetUrl}
            title="HelloSir.ai live agent"
            allow="microphone; autoplay; clipboard-read; clipboard-write"
            className="w-full h-full border-0"
            data-testid="landing-chat-bubble-iframe"
          />
        </div>
      </div>
    </>
  );
}
